/* eslint-disable consistent-return */
import React, { useCallback, useState, useEffect } from 'react';
import {
  SmlsAutoCompleteSearch,
  SmlsCalendar,
  utils,
} from '@smiles/smiles-ui-kit-react';

import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useSearch } from '../../hooks/search';

import {
  getAirportFormattedValue,
  searchAirport,
} from '../../common/searchUtils';
import { exploreWorldItem } from '../../common/constants';

import ISearchItem from './types';
import { flightTypesList, ICustomAutoCompleteSearch } from '../../common/types';

import { handleGetHolidays } from '../../services/handleGetHolidays';
import { useGeoLocation } from '../../hooks/useGeoLocation';

const SearchItem = ({ type }: ISearchItem) => {
  const { t } = useTranslation('flightSearch');
  const {
    origins,
    originsHistory,
    destinationsHistory,
    destinations,
    setOrigins,
    setDestinations,
    setFlightDates,
    setSearchErrors,
    searchErrors,
    flightType,
    flightDates,
    isMultiCityFlight,
    hasSomeSearchError,
    geolocationSuggestion,
  } = useSearch();
  const { latitude: lat, longitude: long } = useGeoLocation();

  const isFirstLeg = type === 'firstLeg';

  const [originValue, setOriginValue] = useState(() => {
    return getAirportFormattedValue({ isFirstLeg, flight: origins });
  });

  const [destinationValue, setDestinationValue] = useState(() => {
    return getAirportFormattedValue({
      isFirstLeg,
      flight: destinations,
    });
  });

  /* istanbul ignore next */
  useEffect(() => {
    if (flightType === 'MULTI_CITY' && destinationValue === t('exploreWorld')) {
      setDestinationValue('');
    }
  }, [flightType]);

  useEffect(() => {
    if (origins) {
      const updateOriginValue = getAirportFormattedValue({
        isFirstLeg,
        flight: origins,
      });
      setOriginValue(updateOriginValue);
    }
    if (destinations) {
      const updateDestinationValue = getAirportFormattedValue({
        isFirstLeg,
        flight: destinations,
      });
      setDestinationValue(updateDestinationValue);
    }
  }, [origins, destinations, isFirstLeg]);

  const [isDestinationFocused, setIsDestinationFocused] = useState(false);
  const [isDateFocused, setIsDateFocused] = useState<boolean | undefined>(
    false
  );

  const checkItemDisabled = useCallback(
    (itemType: 'origin' | 'destination', item: ICustomAutoCompleteSearch) => {
      const otherOrigDest = itemType === 'origin' ? destinations : origins;
      const itemToCompare =
        type === 'firstLeg'
          ? otherOrigDest?.firstLeg?.code
          : otherOrigDest?.secondLeg?.code;

      return item.code === itemToCompare;
    },
    [origins, destinations, type]
  );

  /* istanbul ignore next */
  const handleSearch = useCallback(
    async (itemType: 'origin' | 'destination', value: string) => {
      const airports = await searchAirport({
        location: {
          latitude: lat,
          longitude: long,
        },
        filter: {
          value,
          field: 'description',
          itemType,
        },
      });

      return airports.map((item) => {
        if (!item.code) {
          // eslint-disable-next-line no-param-reassign
          item.subtitle = ' ';
        }
        return item;
      });
    },
    [lat, long]
  );

  async function getFirstSuggestion() {
    if (originValue !== '' || !lat || type === 'secondLeg' || !long) {
      return;
    }

    const aiportSuggestion = await searchAirport({
      location: {
        latitude: lat,
        longitude: long,
      },
    });

    setOrigins({
      firstLeg: aiportSuggestion[0],
    });
  }

  useEffect(() => {
    getFirstSuggestion();
  }, [lat, long]);

  const handleChangeCalendar = useCallback(
    (dates) => {
      const updateDate =
        type === 'firstLeg'
          ? {
              firstFlight: {
                departureDate: dates.startDate?.toDate(),
                returnDate: dates.endDate?.toDate(),
              },
            }
          : {
              secondFlightDate: dates.startDate?.toDate(),
            };

      setFlightDates((prev) => ({
        ...prev,
        ...updateDate,
      }));

      setSearchErrors((prev) => {
        return type === 'firstLeg'
          ? { ...prev, firstLeg: { ...prev?.firstLeg, date: false } }
          : { ...prev, secondLeg: { ...prev?.secondLeg, date: false } };
      });
    },
    [type, setFlightDates, setSearchErrors]
  );

  const handleSelectOrigin = useCallback(
    (origin: ICustomAutoCompleteSearch) => {
      const selectedOrigin =
        type === 'firstLeg' ? { firstLeg: origin } : { secondLeg: origin };

      setSearchErrors((prev) => {
        return type === 'firstLeg'
          ? { ...prev, firstLeg: { ...prev?.firstLeg, origin: false } }
          : { ...prev, secondLeg: { ...prev?.secondLeg, origin: false } };
      });

      setOrigins((prev) => ({ ...prev, ...selectedOrigin }));
    },
    [type, setOrigins, setSearchErrors]
  );

  const handleSelectDestination = useCallback(
    (destination: ICustomAutoCompleteSearch) => {
      const selectedDestination =
        type === 'firstLeg'
          ? { firstLeg: destination }
          : { secondLeg: destination };

      setSearchErrors((prev) => {
        return type === 'firstLeg'
          ? { ...prev, firstLeg: { ...prev?.firstLeg, destination: false } }
          : { ...prev, secondLeg: { ...prev?.secondLeg, destination: false } };
      });
      setDestinations((prev) => ({ ...prev, ...selectedDestination }));
    },
    [type, setDestinations, setSearchErrors]
  );

  const handleIsValid = useCallback(
    (itemType: 'origin' | 'destination', valid: boolean) => {
      if (itemType === 'origin') {
        setSearchErrors((prev) => {
          return type === 'firstLeg'
            ? { ...prev, firstLeg: { ...prev?.firstLeg, origin: !valid } }
            : { ...prev, secondLeg: { ...prev?.secondLeg, origin: !valid } };
        });
      } else {
        setSearchErrors((prev) => {
          return type === 'firstLeg'
            ? { ...prev, firstLeg: { ...prev?.firstLeg, destination: !valid } }
            : {
                ...prev,
                secondLeg: { ...prev?.secondLeg, destination: !valid },
              };
        });
      }
    },
    [type, setSearchErrors]
  );

  function getRangeDateAllowed(day: moment.Moment): boolean {
    const initDate = isFirstLeg
      ? new Date()
      : flightDates?.firstFlight?.departureDate;

    const firstDay = moment(initDate);
    const lastDay = moment().add(329, 'days');

    return (
      !utils.isInclusivelyAfterDay(day, firstDay) ||
      !utils.isInclusivelyBeforeDay(day, lastDay)
    );
  }

  const handleOnBlur = useCallback(
    (itemType: 'origin' | 'destination', val: string) => {
      if (itemType === 'origin') {
        if (val !== originValue && val !== '') {
          handleIsValid(itemType, false);
        }
      } else if (val !== destinationValue && val !== '') {
        handleIsValid(itemType, false);
      }
    },
    [originValue, destinationValue, handleIsValid]
  );

  const handleOnStartSearch = useCallback(
    (itemType: 'origin' | 'destination', value: string) => {
      const doSearch = value.length > 2;
      if (!doSearch) {
        if (value.length === 0) {
          const unset =
            type === 'firstLeg'
              ? { firstLeg: undefined }
              : { secondLeg: undefined };
          if (itemType === 'origin') {
            setOrigins((prev) => ({ ...prev, ...unset }));
          } else {
            setDestinations((prev) => ({ ...prev, ...unset }));
          }
          handleIsValid(itemType, true);
        } else {
          handleIsValid(itemType, false);
        }
      }
      return doSearch;
    },
    [type, setOrigins, setDestinations, handleIsValid]
  );

  const originAutoCompleteForceError = isFirstLeg
    ? searchErrors?.firstLeg?.origin
    : searchErrors?.secondLeg?.origin;

  const destinationAutoCompleteForceError = isFirstLeg
    ? searchErrors?.firstLeg?.destination
    : searchErrors?.secondLeg?.destination;

  const calendarForceError = isFirstLeg
    ? searchErrors?.firstLeg?.date
    : searchErrors?.secondLeg?.date;

  useEffect(() => {
    if (!isDateFocused) {
      return undefined;
    }

    const resetDateFocus = setTimeout(() => {
      setIsDateFocused(undefined);
    }, 1000);

    return () => {
      clearTimeout(resetDateFocus);
    };
  }, [isDateFocused]);

  useEffect(() => {
    if (!isDestinationFocused) {
      return undefined;
    }

    const resetDestinationFocus = setTimeout(() => {
      setIsDestinationFocused(false);
    }, 1000);

    return () => {
      clearTimeout(resetDestinationFocus);
    };
  }, [isDestinationFocused]);

  const originInitialList = [
    {
      label: t('lastSearchTitle'),
      labelId: 'lbl_lastSearch',
      items: originsHistory || [],
    },
    {
      label: t('geolocationSuggestionTitle'),
      labelId: 'lbl_suggestions',
      items: geolocationSuggestion?.slice(0, 3) || [],
    },
  ];

  const destinationInitialList = [];

  if (origins?.firstLeg && !isMultiCityFlight) {
    destinationInitialList.push({
      label: t('exploreWorld'),
      items: [exploreWorldItem] as ICustomAutoCompleteSearch[],
    });
  }

  if (destinationsHistory) {
    if (!destinationInitialList.length) {
      const items = [];

      if (!isMultiCityFlight) {
        items.push(exploreWorldItem as ICustomAutoCompleteSearch);
      }

      items.push(...destinationsHistory);

      destinationInitialList.push({
        label: t('lastSearchTitle'),
        items,
      });
    } else {
      destinationInitialList[0].label = t('lastSearchTitle');
      destinationInitialList[0].items.push(...destinationsHistory);
    }
  }

  const isOneWayOrMultiCity =
    flightType === flightTypesList.ONE_WAY || isMultiCityFlight;

  const startDateInitialValue =
    type === 'firstLeg'
      ? flightDates?.firstFlight?.departureDate?.toISOString().split('T')[0]
      : flightDates?.secondFlightDate?.toISOString().split('T')[0] || undefined;

  const endDateInitialValue =
    flightDates?.firstFlight && type === 'firstLeg'
      ? flightDates?.firstFlight?.returnDate?.toISOString().split('T')[0]
      : undefined;

  return (
    <div
      className={`search-item ${hasSomeSearchError ? 'search-item-error' : ''}`}
      data-testid="search-item"
    >
      <div className="location-section">
        <SmlsAutoCompleteSearch<ICustomAutoCompleteSearch>
          icon={<i className="material-icons">flight_takeoff</i>}
          errorMessage={t('originErrorMessage')}
          placeholder={t('origin')}
          notFoundTitle={t('notFoundTitle')}
          notFoundSubtitle={t('notFoundSubtitle')}
          expandOnEnter
          onSearch={(search) => handleSearch('origin', search)}
          onFocusMobilePlaceholder={t('originMobile')}
          onFocusDesktopPlaceholder={t('onFocusOriginPlaceholder')}
          initialList={originInitialList}
          value={originValue}
          inputId={`inp_flightOrigin_${isFirstLeg ? '1' : '2'}`}
          inputErrorId={`lbl_msgInvalid_${isFirstLeg ? '1' : '4'}`}
          loadingLogoId="icon_loading"
          notFoundSearchTextId="lbl_msgSearchNotFound"
          onStartSearch={(search) => handleOnStartSearch('origin', search)}
          renderItemDisabled={(item) => checkItemDisabled('origin', item)}
          onSelectedItem={(result) => {
            setOriginValue(' ');
            handleSelectOrigin(result);
            setIsDestinationFocused(true);
          }}
          onValid={(valid) => handleIsValid('origin', valid)}
          onBlur={(val) => handleOnBlur('origin', val)}
          forceError={originAutoCompleteForceError}
        />
        <div className="flight-icon-circle">
          {isOneWayOrMultiCity ? (
            <i className="material-icons">flight</i>
          ) : (
            <i className="smls-icon-round-trip" />
          )}
        </div>
        <SmlsAutoCompleteSearch<ICustomAutoCompleteSearch>
          icon={<i className="material-icons">place</i>}
          errorMessage={t('destinationErrorMessage')}
          placeholder={t('destination')}
          notFoundTitle={t('notFoundTitle')}
          notFoundSubtitle={t('notFoundSubtitle')}
          expandOnEnter
          onSearch={(search) => handleSearch('destination', search)}
          onFocusMobilePlaceholder={t('destinationMobile')}
          onFocusDesktopPlaceholder={t('onFocusDestinationPlaceholder')}
          initialList={destinationInitialList}
          value={destinationValue}
          inputId={`inp_flightDestination_${isFirstLeg ? '1' : '2'}`}
          inputErrorId={`lbl_msgInvalid_${isFirstLeg ? '2' : '5'}`}
          loadingLogoId="icon_loading"
          notFoundSearchTextId="lbl_msgSearchNotFound"
          forceFocus={isDestinationFocused}
          onStartSearch={(search) => handleOnStartSearch('destination', search)}
          renderItemDisabled={(item) => checkItemDisabled('destination', item)}
          onSelectedItem={(result) => {
            setDestinationValue(' ');
            handleSelectDestination(result);
            setIsDateFocused(true);
          }}
          onValid={(valid) => handleIsValid('destination', valid)}
          onBlur={(val) => handleOnBlur('destination', val)}
          forceError={destinationAutoCompleteForceError}
        />
      </div>
      <div className="date-section">
        <SmlsCalendar
          id={`calendar-date-${isFirstLeg ? '3' : '6'}`}
          startDateId="startDateId"
          endDateId="endDateId"
          errorMessage={
            isOneWayOrMultiCity ? t('dateErrorMessage') : t('datesErrorMessage')
          }
          startDatePlaceholderText={
            isOneWayOrMultiCity
              ? t('calendar.mobileSingularPlaceholder')
              : t('startDate')
          }
          datesListHighlight={handleGetHolidays()}
          endDatePlaceholderText={t('endDate')}
          onChange={handleChangeCalendar}
          mobilePlaceholderText={
            isOneWayOrMultiCity
              ? t('calendar.mobileSingularPlaceholder')
              : t('calendar.mobilePluralPlaceholder')
          }
          startDateValue={startDateInitialValue}
          startDateInitialValue={startDateInitialValue}
          endDateInitialValue={endDateInitialValue}
          idConfirmButton="btn_confirmCalendar"
          idNextIconButton="btn_nextCalendar"
          idErrorMessage={`lbl_msgInvalid_${isFirstLeg ? '3' : '6'}`}
          isSingleDate={isOneWayOrMultiCity}
          forceError={calendarForceError}
          forceFocus={isDateFocused}
          minimumNights={0}
          daysHoverText="Volta no mesmo dia"
          maxDate={11}
          minDate={0}
          numberOfVerticalMonths={12}
          isOutsideRange={(day) => getRangeDateAllowed(day)}
        />
      </div>
    </div>
  );
};

export default SearchItem;
